import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { toast } from 'react-hot-toast'
import { adminService, CategoryTranslation } from '../services/adminService'
import { useTranslation } from '../i18n/hooks/useTranslation'
import { Button } from './ui/Button'

interface CategoryTranslationModalProps {
  categoryId: string
  onClose: () => void
}

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'fr', name: 'French' },
  { code: 'es', name: 'Spanish' },
  { code: 'de', name: 'German' },
]

export function CategoryTranslationModal({ categoryId, onClose }: CategoryTranslationModalProps) {
  const { t } = useTranslation()
  const [editingTranslation, setEditingTranslation] = useState<CategoryTranslation | null>(null)
  const queryClient = useQueryClient()

  const { data: translations = [], isLoading } = useQuery({
    queryKey: ['categoryTranslations', categoryId],
    queryFn: () => adminService.getCategoryTranslations(categoryId),
  })

  const { register, handleSubmit, reset, formState: { errors } } = useForm<{
    language: string
    name: string
    description: string
    slug: string
  }>()

  const createMutation = useMutation({
    mutationFn: (translation: Omit<CategoryTranslation, 'id'>) =>
      adminService.createCategoryTranslation(translation),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoryTranslations', categoryId] })
      toast.success(t('translations.notifications.createdSuccess'))
      reset()
    },
    onError: () => {
      toast.error(t('translations.notifications.createFailed'))
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, updates }: { id: string; updates: Partial<CategoryTranslation> }) =>
      adminService.updateCategoryTranslation(id, updates),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoryTranslations', categoryId] })
      toast.success(t('translations.notifications.updatedSuccess'))
      setEditingTranslation(null)
      reset()
    },
    onError: () => {
      toast.error(t('translations.notifications.updateFailed'))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminService.deleteCategoryTranslation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['categoryTranslations', categoryId] })
      toast.success(t('translations.notifications.deletedSuccess'))
    },
    onError: () => {
      toast.error(t('translations.notifications.deleteFailed'))
    },
  })

  const onSubmit = (data: { language: string; name: string; description: string; slug: string }) => {
    if (editingTranslation) {
      updateMutation.mutate({
        id: editingTranslation.id,
        updates: { ...data }
      })
    } else {
      createMutation.mutate({
        categoryId,
        ...data
      })
    }
  }

  const handleEdit = (translation: CategoryTranslation) => {
    setEditingTranslation(translation)
    reset({
      language: translation.language,
      name: translation.name,
      description: translation.description
    })
  }

  const handleDelete = (id: string) => {
    if (confirm(t('translations.actions.deleteConfirm'))) {
      deleteMutation.mutate(id)
    }
  }

  const getLanguageName = (code: string) => {
    return LANGUAGES.find(lang => lang.code === code)?.name || code
  }

  const existingLanguages = translations.map(t => t.language)
  const availableLanguages = LANGUAGES.filter(lang => !existingLanguages.includes(lang.code))

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-900">
              {t('translations.categoryTranslations')}: {categoryId}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Translation Form */}
          <div className="mb-8">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {editingTranslation ? t('translations.editTranslation') : t('translations.addTranslation')}
            </h3>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('translations.fields.language')}
                </label>
                <select
                  {...register('language', { required: t('translations.validation.languageRequired') })}
                  disabled={!!editingTranslation}
                  className="w-full pl-3 pr-8 py-2 border border-gray-300 rounded-full focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-gray-100"
                >
                  <option value="">{t('translations.fields.selectLanguage')}</option>
                  {(editingTranslation ? LANGUAGES : availableLanguages).map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.name}
                    </option>
                  ))}
                </select>
                {errors.language && (
                  <p className="text-red-500 text-sm mt-1">{errors.language.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('translations.fields.name')}
                </label>
                <input
                  type="text"
                  {...register('name', { required: t('translations.validation.nameRequired') })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {errors.name && (
                  <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('translations.fields.description')}
                </label>
                <textarea
                  rows={3}
                  {...register('description', { required: t('translations.validation.descriptionRequired') })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                {errors.description && (
                  <p className="text-red-500 text-sm mt-1">{errors.description.message}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('categories.fields.slug')} ({t('categories.fields.slugHelp')})
                </label>
                <input
                  type="text"
                  {...register('slug', { required: t('categories.validation.slugRequired') })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="url-friendly-name"
                />
                {errors.slug && (
                  <p className="text-red-500 text-sm mt-1">{errors.slug.message}</p>
                )}
              </div>

              <div className="flex justify-end space-x-3">
                {editingTranslation && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditingTranslation(null)
                      reset()
                    }}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
                  >
                    {t('common.cancel')}
                  </button>
                )}
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 bg-primary-600 text-white rounded-full hover:bg-primary-700 disabled:opacity-50 transition-colors"
                >
                  {createMutation.isPending || updateMutation.isPending
                    ? t('common.loading')
                    : editingTranslation
                    ? t('common.update')
                    : t('common.create')
                  }
                </button>
              </div>
            </form>
          </div>

          {/* Existing Translations */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('translations.existingTranslations')} ({translations.length})
            </h3>
            
            {isLoading ? (
              <div className="text-center py-8 text-gray-500">{t('common.loading')}</div>
            ) : translations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('translations.noTranslationsFound')}
              </div>
            ) : (
              <div className="space-y-4">
                {translations.map((translation) => (
                  <div key={translation.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center mb-2">
                          <span className="text-sm font-medium text-gray-900">
                            {getLanguageName(translation.language)}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
                            {translation.language}
                          </span>
                        </div>
                        <h4 className="text-lg font-medium text-gray-900 mb-1">
                          {translation.name}
                        </h4>
                        <p className="text-gray-600 text-sm mb-1">
                          {translation.description}
                        </p>
                      </div>
                      <div className="flex space-x-2 ml-4">
                        <Button
                          onClick={() => handleEdit(translation)}
                          variant="tertiary"
                          size="sm"
                        >
                          {t('common.edit')}
                        </Button>
                        <button
                          onClick={() => handleDelete(translation.id)}
                          className="text-red-600 hover:text-red-900 text-sm"
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
} 